import React, { useEffect, useRef, useState } from 'react';
import { NavLink as RouterLink, Outlet, useLocation, useNavigate } from 'react-router-dom';
import { signOut } from 'firebase/auth';
import { auth } from '../firebase';
import { useAuth } from '../context/AuthContext';
import ThemeDevSwitcher from './ThemeDevSwitcher';

const Layout = () => {
  const { userProfile } = useAuth();
  const isAdmin = !!userProfile?.isAdmin || userProfile?.role === 'admin';
  const navigate = useNavigate();
  const location = useLocation();
  const navRef = useRef(null);

  const isStatsRoute = location.pathname.startsWith('/stats/');
  const isStandingsRoute = location.pathname === '/standings';
  const isConstrainedContentRoute = isStatsRoute || isStandingsRoute;

  const mainClassName = isConstrainedContentRoute
    ? 'flex-grow w-full min-w-0 container mx-auto p-2 sm:p-3 md:p-4'
    : 'flex-grow w-full min-w-0 mx-auto max-w-screen-xl px-2 sm:px-4 md:px-6 py-3 sm:py-4';
  const cardClassName = isConstrainedContentRoute
    ? 'card w-full min-w-0 rounded-lg shadow-xl p-3 sm:p-4 md:p-6 min-h-[60vh]'
    : 'card w-full min-w-0 rounded-lg shadow-xl p-3 sm:p-4 md:p-6 min-h-[60vh]';

  // Remove system/browser dark-mode coupling; ThemeDevSwitcher controls palette via data-theme.

  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isLeagueOpen, setIsLeagueOpen] = useState(false);
  const [isPointSeriesOpen, setIsPointSeriesOpen] = useState(false);
  const [isStatsOpen, setIsStatsOpen] = useState(false);
  const [showCookieBanner, setShowCookieBanner] = useState(false);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (navRef.current && !navRef.current.contains(event.target) && isMobileMenuOpen) {
        setIsMobileMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isMobileMenuOpen]);

  useEffect(() => {
    const consent = localStorage.getItem('cookieConsent');
    if (!consent) {
      setShowCookieBanner(true);
    }
  }, []);

  const linkItemClass = 'block w-full py-2 px-4 nav-item rounded-md transition duration-200 ease-in-out cursor-pointer';
  const midLinkItemClass = 'block w-full py-2 pl-6 pr-4 nav-item rounded-md transition duration-200 ease-in-out cursor-pointer';
  const subLinkItemClass = 'block w-full py-2 pl-8 pr-4 nav-item rounded-md transition duration-200 ease-in-out cursor-pointer';
  const closeMenus = () => {
    setIsMobileMenuOpen(false);
    setIsLeagueOpen(false);
    setIsPointSeriesOpen(false);
    setIsStatsOpen(false);
  };
  const handleLogout = async () => {
    await signOut(auth);
    closeMenus();
    navigate('/');
  };

  const handleCookieConsent = () => {
    localStorage.setItem('cookieConsent', 'true');
    setShowCookieBanner(false);
  };

  return (
    <div className="min-h-screen main-surface flex flex-col">
      <header className="header-bar shadow-md p-4 sticky top-0 z-50">
        <nav ref={navRef} className="container mx-auto flex justify-between items-center relative">
          <div className="flex items-center gap-3 min-w-0">
            <h1 className="text-2xl font-bold truncate">Chico Darts</h1>
            {(userProfile?.role === 'member' || isAdmin) && (
              <div className="flex items-center">
                <ThemeDevSwitcher inline />
              </div>
            )}
          </div>
          <div className="flex items-center space-x-4 z-10">
            {/* Theme palette switcher is handled via ThemeDevSwitcher; no system dark-mode toggle. */}
            <button onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)} className="focus:outline-none" aria-label={isMobileMenuOpen ? 'Close menu' : 'Open menu'}>
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d={isMobileMenuOpen ? 'M6 18L18 6M6 6l12 12' : 'M4 6h16M4 12h16m-7 6h7'}></path>
              </svg>
            </button>
          </div>

          <ul className={`absolute top-full right-0 nav-menu py-2 shadow-lg w-48 rounded-md ${isMobileMenuOpen ? 'block' : 'hidden'}`}>
            {!userProfile && (
              <>
                <li><RouterLink to="/" className={`${linkItemClass} nav-item`} onClick={closeMenus}>Home</RouterLink></li>
                <li><RouterLink to="/about" className={`${linkItemClass} nav-item`} onClick={closeMenus}>About</RouterLink></li>
              </>
            )}

            {userProfile && (
              <>
                <li><RouterLink to="/dashboard" className={`${linkItemClass} nav-item`} onClick={closeMenus}>Dashboard</RouterLink></li>
                <li><RouterLink to="/profile" className={`${linkItemClass} nav-item`} onClick={closeMenus}>Profile</RouterLink></li>
                <li><RouterLink to="/my-team" className={`${linkItemClass} nav-item`} onClick={closeMenus}>My Team</RouterLink></li>
              </>
            )}

            {isAdmin && (
              <>
                <li><RouterLink to="/admin/team-setup" className={linkItemClass} onClick={closeMenus}>Team Setup</RouterLink></li>
                <li><RouterLink to="/admin/schedule-builder" className={linkItemClass} onClick={closeMenus}>Schedule Builder</RouterLink></li>
                <li><RouterLink to="/admin/playoff-builder" className={linkItemClass} onClick={closeMenus}>Playoff Builder</RouterLink></li>
                <li><RouterLink to="/admin/update-schedule" className={linkItemClass} onClick={closeMenus}>Update Schedule</RouterLink></li>
                <li><RouterLink to="/admin/upload-results" onClick={closeMenus} className="block w-full py-2 px-4 rounded-md nav-cta-success transition duration-200 ease-in-out cursor-pointer">Upload Results</RouterLink></li>
              </>
            )}

            <li><RouterLink to="/schedule" className={linkItemClass} onClick={closeMenus}>Schedule</RouterLink></li>

            <li className="relative group">
              <div className="flex items-center justify-between py-2 px-4 nav-item rounded-md transition duration-200 ease-in-out cursor-pointer" onClick={() => setIsLeagueOpen(!isLeagueOpen)}>
                League
                <svg className={`w-4 h-4 ml-2 transform ${isLeagueOpen ? 'rotate-180' : ''} transition-transform`} fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clipRule="evenodd"></path></svg>
              </div>
              {isLeagueOpen && (
                <ul className="rounded-md mt-1 w-full nav-menu">
                  <li><RouterLink to="/league/sign-up" className={midLinkItemClass} onClick={closeMenus}>Sign Up Info</RouterLink></li>
                  <li><RouterLink to="/standings" className={midLinkItemClass} onClick={closeMenus}>Standings</RouterLink></li>
                  {userProfile && (
                    <>
                      <li><RouterLink to="/teams" className={midLinkItemClass} onClick={closeMenus}>Teams</RouterLink></li>
                      <li><RouterLink to="/members" className={midLinkItemClass} onClick={closeMenus}>Members</RouterLink></li>
                    </>
                  )}
                  <li>
                    <div className="flex items-center justify-between py-2 pl-6 pr-4 nav-item rounded-md transition duration-200 ease-in-out cursor-pointer" onClick={() => setIsStatsOpen(!isStatsOpen)}>
                      Stats
                      <svg className={`w-4 h-4 ml-2 transform ${isStatsOpen ? 'rotate-180' : ''} transition-transform`} fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clipRule="evenodd"></path></svg>
                    </div>
                    {isStatsOpen && (
                      <ul className="rounded-md mt-1 nav-menu">
                        {userProfile && (
                          <li><RouterLink to="/stats/players" className={subLinkItemClass} onClick={closeMenus}>Player Stats Table</RouterLink></li>
                        )}
                        {userProfile && (
                          <li><RouterLink to="/stats/player-stats-cards" className={subLinkItemClass} onClick={closeMenus}>Player Stats Cards</RouterLink></li>
                        )}  
                        <li><RouterLink to="/stats/teams" className={subLinkItemClass} onClick={closeMenus}>Team Stats Table</RouterLink></li>
                        <li><RouterLink to="/stats/team-stats-cards" className={subLinkItemClass} onClick={closeMenus}>Team Stats Cards</RouterLink></li>
                        {userProfile && (
                          <li><RouterLink to="/stats/compare" className={subLinkItemClass} onClick={closeMenus}>Compare Stats</RouterLink></li>
                        )}
                      </ul>
                    )}
                  </li>
                  {/* {userProfile && (
                    <li>
                      <div className="flex items-center justify-between py-2 pl-6 pr-4 nav-item rounded-md transition duration-200 ease-in-out cursor-pointer" onClick={() => setIsArchiveOpen(!isArchiveOpen)}>
                        Archive
                        <svg className={`w-4 h-4 ml-2 transform ${isArchiveOpen ? 'rotate-180' : ''} transition-transform`} fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clipRule="evenodd"></path></svg>
                      </div>
                      {isArchiveOpen && (
                        <ul className="rounded-md mt-1 nav-menu">
                          <li><RouterLink to="/archive/players" className={subLinkItemClass} onClick={closeMenus}>Players</RouterLink></li>
                          <li><RouterLink to="/archive/highlights" className={subLinkItemClass} onClick={closeMenus}>Highlights</RouterLink></li>
                        </ul>
                      )}
                    </li>
                  )} */}
                </ul>
              )}
            </li>

            <li className="relative group">
              <div className="flex items-center justify-between py-2 px-4 nav-item rounded-md transition duration-200 ease-in-out cursor-pointer" onClick={() => setIsPointSeriesOpen(!isPointSeriesOpen)}>
                Point Series
                <svg className={`w-4 h-4 ml-2 transform ${isPointSeriesOpen ? 'rotate-180' : ''} transition-transform`} fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clipRule="evenodd"></path></svg>
              </div>
              {isPointSeriesOpen && (
                <ul className="nav-menu rounded-md mt-1 w-full">
                  <li><RouterLink to="/point-series" className={midLinkItemClass} onClick={closeMenus}>Overview</RouterLink></li>
                  <li><RouterLink to="/leaderboard" className={midLinkItemClass} onClick={closeMenus}>Leaderboard</RouterLink></li>
                  {isAdmin && (
                    <li><RouterLink to="/admin/point-series-tools" className={midLinkItemClass} onClick={closeMenus}>Series Tools</RouterLink></li>
                  )}
                </ul>
              )}
            </li>

            {!userProfile && (
              <li><RouterLink to="/contact" className={linkItemClass} onClick={closeMenus}>Contact Us</RouterLink></li>
            )}

            {isAdmin && (
              <>
                <li><RouterLink to="/admin/settings" className={linkItemClass} onClick={closeMenus}>Settings</RouterLink></li>
              </>
            )}

            {userProfile ? (
              <li>
                <button className="w-full text-left px-4 py-2 nav-cta-danger rounded-md transition duration-200 ease-in-out" onClick={handleLogout}>
                  Log Out ({userProfile?.nickname || userProfile?.firstName || (isAdmin ? 'admin' : 'member')})
                </button>
              </li>
            ) : (
              <li><RouterLink to="/login" className={linkItemClass} onClick={closeMenus}>Login</RouterLink></li>
            )}
          </ul>
        </nav>
      </header>

      <main className={mainClassName}>
        <div className={cardClassName}>
          <Outlet />
        </div>
      </main>

      {showCookieBanner && (
        <div className="fixed bottom-0 left-0 right-0 z-[200] p-4 bg-[var(--nav-surface-bg)] border-t border-low shadow-lg animate-slide-up">
          <div className="container mx-auto flex flex-col sm:flex-row items-center justify-between gap-y-3 gap-x-6">
            <div className="flex items-center gap-4">
              <svg className="hidden sm:block w-8 h-8 text-muted shrink-0" fill="currentColor" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM7 8a1 1 0 100-2 1 1 0 000 2zm0 4a1 1 0 100-2 1 1 0 000 2zm4-4a1 1 0 100-2 1 1 0 000 2zm4 0a1 1 0 100-2 1 1 0 000 2zm-4 4a1 1 0 100-2 1 1 0 000 2z" clipRule="evenodd" />
              </svg>
              <p className="text-sm text-muted text-center sm:text-left">
                This website uses cookies to ensure you get the best experience. We do not share or sell any data but by using this site, you agree to our use of cookies. Please review our <RouterLink to="/privacy-policy" className="text-info hover:underline">Privacy Policy</RouterLink> for more details.
              </p>
            </div>
            <button
              onClick={handleCookieConsent}
              className="btn btn-primary py-2 px-6 rounded-lg shadow-md shrink-0"
            >
              Got it!
            </button>
          </div>
        </div>
      )}

      <footer className="footer-bar p-4 text-center text-xs sm:text-sm mt-auto">
        <div className="container mx-auto text-muted">
            <span className="block sm:inline">&copy; {new Date().getFullYear()} Chico Area Dart Association. All Rights Reserved.</span>
            <span className="hidden sm:inline mx-2" aria-hidden="true">&bull;</span>
            <div className="block sm:inline mt-2 sm:mt-0">
                <RouterLink to="/privacy-policy" className="hover:text-strong transition-colors">Privacy Policy</RouterLink>
                <span className="mx-2">&bull;</span>
                <RouterLink to="/terms-of-use" className="hover:text-strong transition-colors">Terms of Use</RouterLink>
            </div>
        </div>
      </footer>
    </div>
  );
};

export default Layout;
